import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { SEO } from "../components/SEO";
import { Reveal } from "../components/Reveal";
import { CTASection } from "../components/CTASection";
import { getArticle, relatedArticles } from "../data/articles";
import { getProduct } from "../data/content";
import { trackEvent, EVENTS } from "../services/analytics";

export default function ArticlePage() {
  const { slug } = useParams();
  const article = getArticle(slug);

  useEffect(() => {
    if (article) trackEvent(EVENTS.ARTICLE_VIEW, { slug: article.slug });
  }, [article]);

  if (!article) return <Navigate to="/404" replace />;

  const related = relatedArticles(article.slug);
  const product = article.relatedProduct ? getProduct(article.relatedProduct) : null;

  return (
    <>
      <SEO
        title={`${article.title} | ZasyaOnline Insights`}
        description={article.excerpt}
        path={`/insights/${article.slug}`}
      />
      <article className="pt-28 pb-16 sm:pt-36" data-testid="article-page">
        <div className="container-x max-w-3xl">
          <Reveal>
            <Link
              to="/insights"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 hover:text-blue-800"
              data-testid="article-back-link"
            >
              <ArrowLeft size={15} aria-hidden="true" /> All insights
            </Link>
            <div className="mt-6 flex flex-wrap items-center gap-3 text-xs text-slate-500">
              <span className="font-mono uppercase tracking-[0.18em] text-teal-700">{article.category}</span>
              <span aria-hidden="true">·</span>
              <span>
                {new Date(article.date).toLocaleDateString("en-IN", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </span>
              <span aria-hidden="true">·</span>
              <span>{article.author}</span>
            </div>
            <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight text-slate-900 sm:text-4xl">
              {article.title}
            </h1>
            <p className="mt-4 text-lg leading-relaxed text-slate-600">{article.excerpt}</p>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="container-x mt-10 max-w-4xl">
            <div className="clip-frame overflow-hidden rounded-3xl border border-slate-200">
              <img src={article.image} alt={article.imageAlt} className="h-64 w-full object-cover sm:h-96" loading="eager" />
            </div>
          </div>
        </Reveal>

        <div className="container-x mt-12 max-w-3xl" data-testid="article-body">
          {article.body.map((para, i) => (
            <Reveal key={i} delay={Math.min(i * 0.03, 0.2)}>
              <p className="mb-6 text-base leading-relaxed text-slate-700">{para}</p>
            </Reveal>
          ))}

          {product && (
            <Reveal>
              <div className="mt-10 rounded-2xl border border-blue-100 bg-blue-50/60 p-6" data-testid="article-related-product">
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-blue-700">
                  Related product
                </span>
                <h2 className="mt-2 font-display text-lg font-bold text-slate-900">{product.name}</h2>
                <p className="mt-1 text-sm text-slate-600">{product.shortDescription}</p>
                <Link
                  to={product.path}
                  className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 hover:text-blue-800"
                  data-testid="article-product-link"
                >
                  Explore {product.name} <ArrowRight size={14} aria-hidden="true" />
                </Link>
              </div>
            </Reveal>
          )}

          <Reveal>
            <div className="mt-12 border-t border-slate-200 pt-8">
              <h2 className="font-display text-lg font-bold text-slate-900">Keep reading</h2>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/insights/${r.slug}`}
                    className="card-line p-5 transition-[border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-blue-500/60"
                    data-testid={`article-related-${r.slug}`}
                  >
                    <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-teal-700">
                      {r.category}
                    </span>
                    <h3 className="mt-1.5 font-display text-sm font-bold leading-snug text-slate-900">
                      {r.title}
                    </h3>
                  </Link>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </article>
      <CTASection />
    </>
  );
}
